import React, { useEffect, useState } from 'react';
import { Modal, Table, Input, Button, Space, message, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import productConfigService, { ProductConfig, ConfigItem } from '../../services/productConfigService';

const { Title } = Typography;

// Chuyển tiếng Việt có dấu thành không dấu
const removeVietnameseAccents = (str: string) => {
    return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D');
};

const generateValue = (label: string, type: string, existingItems: ConfigItem[]): string => {
    const baseValue = removeVietnameseAccents(label)
        .replace(/[^a-zA-Z0-9x]/g, '') // Giữ lại x cho kích thước
        .toLowerCase();

    const existingValues = new Set(existingItems.map(item => item.value));
    let generatedValue = baseValue;
    let counter = 1;

    while (existingValues.has(generatedValue)) {
        generatedValue = `${baseValue}_${counter}`;
        counter++;
    }

    return generatedValue;
};

interface ProductConfigModalProps {
    visible: boolean;
    onCancel: () => void;
    onSave: () => void;
}

const ProductConfigModal: React.FC<ProductConfigModalProps> = ({ visible, onCancel, onSave }) => {
    const [config, setConfig] = useState<ProductConfig>({
        purposes: [],
        materials: [],
        sizes: [],
        brands: [],
        weaving: [],
        printing: [],
        finishing: [],
        care: []
    });
    const [loading, setLoading] = useState(false);
    const [editingItem, setEditingItem] = useState<{type: keyof ProductConfig, index: number} | null>(null);
    const [editingValue, setEditingValue] = useState('');
    const [editingValueEn, setEditingValueEn] = useState('');

    const fetchConfig = async () => {
        try {
            setLoading(true);
            const data = await productConfigService.getProductConfig();
            setConfig(data);
        } catch (error) {
            message.error('Lỗi khi tải cấu hình');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible) {
            fetchConfig();
        }
    }, [visible]);


    const addItem = (type: keyof ProductConfig, label: string, labelEn: string = '') => {
        const trimmedLabel = label.trim();
        const trimmedLabelEn = labelEn.trim();

        if (!trimmedLabel) {
            message.error('Vui lòng nhập nhãn');
            return;
        }

        // Check label unique
        if (config[type].some(item => item.label === trimmedLabel)) {
            message.error('Label đã tồn tại');
            return;
        }

        const newValue = generateValue(trimmedLabel, type, config[type]);

        // Check value unique (should be guaranteed by generateValue, but double check)
        if (config[type].some(item => item.value === newValue)) {
            message.error('Value đã tồn tại, vui lòng thử label khác');
            return;
        }

        const newItem: ConfigItem = {
            value: newValue,
            label: trimmedLabel,
            label_en: trimmedLabelEn
        };

        setConfig(prev => ({
            ...prev,
            [type]: [...prev[type], newItem]
        }));
    };

    const removeItem = (type: keyof ProductConfig, index: number) => {
        setConfig(prev => ({
            ...prev,
            [type]: prev[type].filter((_, i) => i !== index)
        }));
    };

    const startEdit = (type: keyof ProductConfig, index: number, currentLabel: string, currentLabelEn: string) => {
        setEditingItem({ type, index });
        setEditingValue(currentLabel);
        setEditingValueEn(currentLabelEn);
    };

    const saveEdit = () => {
        if (!editingItem) return;

        const { type, index } = editingItem;
        const trimmedValue = editingValue.trim();
        const trimmedValueEn = editingValueEn.trim();

        if (!trimmedValue) {
            message.error('Vui lòng nhập label');
            return;
        }

        // Check for duplicates
        const existingItems = config[type].filter((_, i) => i !== index);
        if (existingItems.some(item => item.label === trimmedValue)) {
            message.error('Label đã tồn tại');
            return;
        }

        setConfig(prev => ({
            ...prev,
            [type]: prev[type].map((item, i) =>
                i === index
                    ? { ...item, label: trimmedValue, label_en: trimmedValueEn }
                    : item
            )
        }));

        setEditingItem(null);
        setEditingValue('');
        setEditingValueEn('');
    };

    const cancelEdit = () => {
        setEditingItem(null);
        setEditingValue('');
        setEditingValueEn('');
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            await productConfigService.updateProductConfig(config);
            message.success('Đã lưu cấu hình thành công');
            onSave();
        } catch (error) {
            message.error('Lỗi khi lưu cấu hình');
        } finally {
            setLoading(false);
        }
    };

    const renderConfigTable = (type: keyof ProductConfig, title: string) => {

        const columns = [
            {
                title: 'Label',
                dataIndex: 'label',
                key: 'label',
                render: (label: string, record: any, rowIndex: number) => {
                    const isEditing = editingItem?.type === type && editingItem?.index === rowIndex;
                    if (isEditing) {
                        return (
                            <Input
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onPressEnter={saveEdit}
                                autoFocus
                                style={{ width: '100%' }}
                            />
                        );
                    }
                    return label;
                },
            },
            {
                title: 'Label (en)',
                dataIndex: 'label_en',
                key: 'label_en',
                render: (labelEn: string, record: any, rowIndex: number) => {
                    const isEditing = editingItem?.type === type && editingItem?.index === rowIndex;
                    if (isEditing) {
                        return (
                            <Input
                                value={editingValueEn}
                                onChange={(e) => setEditingValueEn(e.target.value)}
                                onPressEnter={saveEdit}
                                style={{ width: '100%' }}
                            />
                        );
                    }
                    return labelEn || '-';
                },
            },
            {
                title: 'Value',
                dataIndex: 'value',
                key: 'value',
                render: (value: string) => (
                    <span style={{ color: '#999' }}>{value}</span>
                ),
            },
            {
                title: 'Thao tác',
                key: 'action',
                width: 120,
                render: (_: any, record: any, index: number) => {
                    const isEditing = editingItem?.type === type && editingItem?.index === index;
                    if (isEditing) {
                        return (
                            <Space>
                                <Button
                                    type="text"
                                    icon={<CheckOutlined />}
                                    onClick={saveEdit}
                                    style={{ color: 'green' }}
                                />
                                <Button
                                    type="text"
                                    icon={<CloseOutlined />}
                                    onClick={cancelEdit}
                                    style={{ color: 'red' }}
                                />
                            </Space>
                        );
                    }
                    return (
                        <Space>
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => startEdit(type, index, record.label, record.label_en || '')}
                                style={{ color: 'blue' }}
                            />
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => removeItem(type, index)}
                            />
                        </Space>
                    );
                },
            },
        ];

        return (
            <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                    <Title level={5}>{title}</Title>
                    <AddItemForm
                        onAdd={(label: string, labelEn: string) => addItem(type, label, labelEn)}
                        existingItems={config[type]}
                    />
                </div>
                <Table
                    columns={columns}
                    dataSource={config[type]}
                    rowKey="value"
                    pagination={false}
                    size="small"
                    bordered
                />
            </div>
        );
    };

    return (
        <Modal
            title="Chỉnh sửa cấu hình sản phẩm"
            open={visible}
            onCancel={onCancel}
            centered
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Hủy
                </Button>,
                <Button key="save" type="primary" loading={loading} onClick={handleSave}>
                    Lưu
                </Button>
            ]}
            width={800}
            style={{ maxHeight: '80vh' }}
            bodyStyle={{ maxHeight: '60vh', overflowY: 'auto', padding: '20px' }}
        >
            <div className="mb-4">
                <p className="text-sm text-gray-600">
                    Cấu hình dùng cho tính năng lọc sản phẩm trên trang chủ.
					Và options chọn trong quản lý sản phẩm.
                </p>
            </div>
            {renderConfigTable('purposes', 'Mục đích sử dụng')}
            {renderConfigTable('materials', 'Chất liệu')}
            {renderConfigTable('sizes', 'Kích thước')}
            {renderConfigTable('brands', 'Thương hiệu')}
            {renderConfigTable('weaving', 'Kỹ thuật dệt')}
            {renderConfigTable('printing', 'Công nghệ in')}
            {renderConfigTable('finishing', 'Hoàn thiện')}
            {renderConfigTable('care', 'Bảo quản')}
        </Modal>
    );
};

const AddItemForm: React.FC<{ onAdd: (label: string, labelEn: string) => void, existingItems: ConfigItem[] }> = ({ onAdd, existingItems }) => {
    const [value, setValue] = useState('');
    const [valueEn, setValueEn] = useState('');

    const handleAdd = () => {
        if (value.trim() && !isLabelExists) {
            onAdd(value, valueEn);
            setValue('');
            setValueEn('');
        }
    };

    const isLabelExists = existingItems.some(item => item.label === value.trim());
    const isDisabled = !value.trim() || isLabelExists;

    return (
        <div className="flex gap-2">
            <Input
                placeholder="Nhập nhãn (VN)"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onPressEnter={handleAdd}
                style={{
                    width: 150,
                    height: 28,
                    borderRadius: 6,
                    borderColor: isLabelExists ? '#ff4d4f' : undefined
                }}
                size="small"
                status={isLabelExists ? 'error' : undefined}
            />
            <Input
                placeholder="Nhập nhãn (EN)"
                value={valueEn}
                onChange={(e) => setValueEn(e.target.value)}
                onPressEnter={handleAdd}
                style={{
                    width: 150,
                    height: 28,
                    borderRadius: 6
                }}
                size="small"
            />
            <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                disabled={isDisabled}
                size="small"
                style={{
                    height: 28,
                    borderRadius: 6
                }}
                className="px-3"
                title={isLabelExists ? 'Label đã tồn tại' : undefined}
            >
                Thêm
            </Button>
        </div>
    );
};

export default ProductConfigModal;
